import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { PlusIcon, UploadIcon } from 'lucide-react';
import { useForm, useWatch, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { useTitle } from 'react-use';

import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { StepWizard, type StepDefinition } from '@/components/organisms/StepWizard/StepWizard';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Button } from '@/components/ui/button';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { Form } from '@/components/ui/form';
import useBrains from '@/hooks/useBrains';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import useWebhooks from '@/hooks/useWebhooks';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { EventName, PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { WebhookType } from '@/shared/types/webhook';
import { TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';
import { extractNames, generateUniqueName } from '@/util/strings';

import { NoWebhooks } from './NoWebhooks';
import { WebhooksTable } from './WebhooksTable/WebhooksTable';
import {
  getDefaultWebhookWizardValues,
  createWebhookWizardSchema,
  type WebhookWizardFormValues,
} from './wizardForm';
import { getWebhookWizardSteps } from './wizardSteps';

export default function Webhooks() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);
  const slug = useSelector(selectAccountSlug);

  const { webhooks, listStatus, createWebhook, createStatus, importWebhook } = useWebhooks(brainId);
  useTitle(t('pages.webhooks', { 0: brain?.name ?? '-' }));

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');

  useEffect(() => {
    setSearch('');
  }, [brainId]);

  const filteredWebhooks = useMemo(() => {
    if (!search.trim()) {
      return webhooks;
    }
    const query = search.toLowerCase();
    return webhooks.filter((webhook) => webhook.name.toLowerCase().includes(query));
  }, [webhooks, search]);

  const isLoading = listStatus === 'pending';
  const { showEmpty, showEmptySkeleton, showList } = useEmptyStateView({
    scope: `webhooks:${brainId}`,
    isLoading,
    isEmpty: webhooks?.length === 0,
  });

  // Extract type from URL params if present
  const searchParams = new URLSearchParams(location.search);
  const typeFromUrl = searchParams.get('type');

  const handleImportClick = useCallback(() => {
    trackEvent(EventName.ClickImportWebhook);
    fileInputRef.current?.click();
  }, []);

  const handleImportWebhook = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target?.files?.[0];
      if (file) {
        importWebhook(file);
        e.target.value = '';
      }
    },
    [importWebhook]
  );

  const webhookWizardSchema = useMemo(
    () => (brainId ? createWebhookWizardSchema(brainId) : null),
    [brainId]
  );

  const form = useForm<WebhookWizardFormValues>({
    resolver: webhookWizardSchema
      ? (yupResolver(webhookWizardSchema) as Resolver<WebhookWizardFormValues>)
      : undefined,
    mode: 'onChange',
    defaultValues: getDefaultWebhookWizardValues(),
  });

  useSegments(PageName.WEBHOOKS);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  // Determine if we should show the creation wizard (route-based)
  const isWizardMode = useMemo(
    () => location.pathname.endsWith('/webhooks/new'),
    [location.pathname]
  );

  const [currentStep, setCurrentStep] = useState(0);

  const exitWizard = useCallback(() => {
    form.reset(getDefaultWebhookWizardValues());
    navigate(`/${slug}/ai-agents/${brainId}/workflows/webhooks`, { replace: true });
  }, [brainId, form, navigate, slug]);

  const startWizard = useCallback(() => {
    const url = `/${slug}/ai-agents/${brainId}/workflows/webhooks/new`;
    navigate(url);
  }, [brainId, navigate, slug]);

  const selectedType = useWatch({ control: form.control, name: 'type' });

  const steps: StepDefinition[] = useMemo(
    () => getWebhookWizardSteps(form, t, selectedType, webhooks),
    [form, t, selectedType, webhooks]
  );

  const handleWizardStepChange = useCallback(
    (next: number) => {
      if (steps[next]?.id === 'details' && !form.getValues('name').trim()) {
        const suggestedName = generateUniqueName(t('webhook.new_webhook'), extractNames(webhooks), {
          hasExtension: false,
        });
        form.setValue('name', suggestedName, { shouldValidate: true, shouldDirty: true });
      }
      setCurrentStep(next);
    },
    [form, steps, t, webhooks]
  );

  const handleWizardComplete = useCallback(async () => {
    await form.handleSubmit(async (values) => {
      const headers = (values.headers ?? [])
        .map((h) => ({ name: h.name?.trim() ?? '', value: h.value ?? '' }))
        .filter((h) => h.name.length > 0)
        .map((h) => ({ name: h.name, value: h.value }));

      createWebhook(
        {
          brain_id: brainId,
          name: values.name,
          url: values.url,
          token: values.token,
          type: values.type,
          headers: values.includeHeaders ? headers : [],
        },
        {
          onSuccess: () => {
            exitWizard();
          },
        }
      );
    })();
  }, [brainId, createWebhook, exitWizard, form]);

  useEffect(() => {
    if (isWizardMode) {
      setCurrentStep(0);
      const wizardType =
        typeFromUrl && Object.values(WebhookType).includes(typeFromUrl as WebhookType)
          ? (typeFromUrl as WebhookType)
          : WebhookType.ACTION;
      form.reset(getDefaultWebhookWizardValues(wizardType));
    }
  }, [form, isWizardMode, typeFromUrl]);

  return (
    <PageContentWrapper readOnly={!canWrite} noGutters>
      {!isWizardMode && (showEmpty || showEmptySkeleton) && (
        <div className={cn(TABLE_LAYOUT_FILL_CLASSES, 'gap-4')}>
          {showEmpty ? (
            <NoWebhooks
              onCreate={startWizard}
              onImport={canWrite ? handleImportWebhook : undefined}
            />
          ) : (
            <EmptySkeleton media="icon" descriptionLines={3} actions={canWrite ? 2 : 1} />
          )}
        </div>
      )}

      {isWizardMode && (
        <div className="px-9 py-5">
          <Form {...form}>
            <StepWizard
              steps={steps}
              currentStep={currentStep}
              onStepChange={handleWizardStepChange}
              onCancel={exitWizard}
              onComplete={handleWizardComplete}
              canProceed={!form.formState.isSubmitting && createStatus !== 'pending'}
              isLoading={form.formState.isSubmitting || createStatus === 'pending'}
              className="w-full"
            >
              <StepWizard.Steps />
            </StepWizard>
          </Form>
        </div>
      )}

      {!isWizardMode && showList && (
        <div className={cn(TABLE_LAYOUT_FILL_CLASSES, 'gap-4')}>
          <div className="flex items-center justify-between">
            <SearchBar
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('webhook.search')}
            />
            {canWrite && (
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json"
                  onChange={handleImportWebhook}
                  className="hidden"
                />
                <Button onClick={handleImportClick} variant="outline-neutral" size="sm">
                  <UploadIcon />
                  {t('common.import')}
                </Button>
                <Button onClick={startWizard} variant="outline-neutral" size="sm">
                  <PlusIcon />
                  {t('common.create')}
                </Button>
              </div>
            )}
          </div>
          <WebhooksTable
            className="min-h-0 flex-1"
            webhooks={filteredWebhooks}
            brainId={brainId}
            canWrite={canWrite}
            isLoading={isLoading || createStatus === 'pending'}
          />
        </div>
      )}
    </PageContentWrapper>
  );
}
